import { KeyRound, Shield, Sliders, UserCheck, UserPlus, UserX } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";

interface AdminUser {
  id: string;
  name: string;
  email: string;
  mobile?: string;
  role: string;
  customPermissions?: Record<string, Record<string, boolean>> | null;
  lastLoginAt: string;
  active: boolean;
}

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin (Unrestricted)" },
  { value: "admin", label: "Admin (Operations Lead)" },
  { value: "retailer_onboarding_executive", label: "Retailer Onboarding Executive" },
  { value: "retailer_onboarding_executive_walletrequesthandler", label: "Retailer Onboarding Executive & Wallet Request Handler" },
  { value: "wallet_request_handler", label: "Wallet Request Handler (Finance)" },
];

const MODULE_LIST = [
  { key: "dashboard", label: "Dashboard overview" },
  { key: "mandates", label: "Mandate master" },
  { key: "installments", label: "Installment schedule" },
  { key: "retailers", label: "Retailer directory" },
  { key: "wallets", label: "Wallet & top-ups" },
  { key: "reports", label: "Reports & registers" },
  { key: "settings", label: "Platform & access settings" },
  { key: "logs", label: "Audit log trail" },
];

const ACTIONS = [
  { key: "view", short: "V", label: "View", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "create", short: "C", label: "Create", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { key: "edit", short: "E", label: "Edit", color: "bg-sky-100 text-sky-700 border-sky-300" },
  { key: "approve", short: "A", label: "Approve", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { key: "delete", short: "D", label: "Delete", color: "bg-rose-100 text-rose-700 border-rose-300" },
];

function normalizeRoleKey(role?: string): string {
  if (!role) return "admin";
  const r = role.toLowerCase().replace(/[\s-]+/g, "_");
  if (r === "superadmin" || r === "super_admin") return "super_admin";
  if (r === "admin") return "admin";
  if (r === "retailer_onboarding_executive") return "retailer_onboarding_executive";
  if (r === "retailer_onboarding_executive_walletrequesthandler") return "retailer_onboarding_executive_walletrequesthandler";
  if (r === "walletrequesthandler" || r === "wallet_request_handler") return "wallet_request_handler";
  return r;
}

function getRoleBadge(role: string) {
  const norm = normalizeRoleKey(role);
  switch (norm) {
    case "super_admin":
      return {
        label: "Super Admin",
        className: "bg-purple-100 text-purple-700 border border-purple-200",
      };
    case "admin":
      return {
        label: "Admin",
        className: "bg-blue-100 text-blue-700 border border-blue-200",
      };
    case "retailer_onboarding_executive":
      return {
        label: "Retailer Onboarding Executive",
        className: "bg-teal-100 text-teal-700 border border-teal-200",
      };
    case "retailer_onboarding_executive_walletrequesthandler":
      return {
        label: "Retailer Onboarding Executive & Wallet Request Handler",
        className: "bg-indigo-100 text-indigo-700 border border-indigo-200",
      };
    case "wallet_request_handler":
      return {
        label: "Wallet Handler",
        className: "bg-amber-100 text-amber-700 border border-amber-200",
      };
    default:
      return {
        label: role,
        className: "bg-gray-100 text-gray-700 border border-gray-200",
      };
  }
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${h}:${m} ${ampm}`;
}

export default function AdminUsersTab() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Add User State
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "admin",
    password: "",
    active: true,
  });

  // Edit User State
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "admin",
    password: "",
    active: true,
  });

  // User Permissions Override Modal State
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permUser, setPermUser] = useState<AdminUser | null>(null);
  const [customPerms, setCustomPerms] = useState<Record<string, Record<string, boolean>>>({});
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  const loadAdminUsers = () => {
    setLoading(true);
    adminService
      .getAdminUsers()
      .then((res) => {
        if (res && res.data && res.data.users) {
          setUsers(res.data.users);
        }
      })
      .catch((err) => {
        toast.error(err.message || "Failed to load staff users");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  function saveAdmin() {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Please fill Name, Email, and Password");
      return;
    }

    setSaving(true);
    adminService
      .createAdminUser(form)
      .then(() => {
        setSaving(false);
        setAddOpen(false);
        loadAdminUsers();
        setForm({ name: "", email: "", mobile: "", role: "admin", password: "", active: true });
        toast.success("Staff user added successfully!");
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err.message || "Failed to add staff user");
      });
  }

  function handleEditClick(user: AdminUser) {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      mobile: user.mobile || "",
      role: normalizeRoleKey(user.role),
      password: "",
      active: user.active,
    });
    setEditOpen(true);
  }

  function saveEditUser() {
    if (!editingUser) return;
    setSaving(true);
    adminService
      .updateAdminUser(editingUser.id, editForm)
      .then(() => {
        setSaving(false);
        setEditOpen(false);
        loadAdminUsers();
        toast.success("Staff user updated successfully!");
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err.message || "Failed to update staff user");
      });
  }

  function toggleActive(user: AdminUser) {
    adminService
      .toggleAdminUser(user.id)
      .then(() => {
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, active: !u.active } : u))
        );
        toast.info(user.active ? "Staff deactivated" : "Staff activated");
      })
      .catch((err) => {
        toast.error(err.message || "Failed to toggle status");
      });
  }

  async function openPermissionsModal(user: AdminUser) {
    setPermUser(user);
    try {
      const res = await adminService.getUserRbac(user.id);
      if (res && res.data) {
        const u = res.data;
        if (u.customPermissions) {
          setCustomPerms(u.customPermissions);
          setOverrideEnabled(true);
        } else {
          setCustomPerms(u.effectivePermissions || {});
          setOverrideEnabled(false);
        }
      }
    } catch {
      setCustomPerms(user.customPermissions || {});
      setOverrideEnabled(Boolean(user.customPermissions));
    }
    setPermModalOpen(true);
  }

  function toggleUserPerm(modKey: string, actionKey: string) {
    if (!overrideEnabled) return;
    setCustomPerms((prev) => {
      const modCopy = { ...(prev[modKey] || { view: false, create: false, edit: false, approve: false, delete: false }) };
      modCopy[actionKey] = !modCopy[actionKey];

      if (modCopy[actionKey] && actionKey !== "view") {
        modCopy.view = true;
      }
      if (actionKey === "view" && !modCopy.view) {
        modCopy.create = false;
        modCopy.edit = false;
        modCopy.approve = false;
        modCopy.delete = false;
      }

      return { ...prev, [modKey]: modCopy };
    });
  }

  async function saveUserPermissions() {
    if (!permUser) return;
    setSavingPerms(true);
    try {
      const payload = overrideEnabled ? customPerms : null;
      await adminService.updateUserRbac(permUser.id, payload);
      toast.success(
        overrideEnabled
          ? `Custom permissions applied for ${permUser.name}`
          : `Reverted to default role permissions for ${permUser.name}`
      );
      setPermModalOpen(false);
      loadAdminUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to update user permissions");
    } finally {
      setSavingPerms(false);
    }
  }

  function generatePassword(target: "add" | "edit") {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789!@#";
    let out = "";
    for (let i = 0; i < 10; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
    if (target === "add") {
      setForm((f) => ({ ...f, password: out }));
    } else {
      setEditForm((f) => ({ ...f, password: out }));
    }
  }

  const columns: Column<AdminUser>[] = [
    {
      key: "name",
      header: "Staff Member",
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[13px] font-semibold text-primary-500">
            {initials(row.name)}
          </span>
          <div>
            <p className="text-sm font-medium text-ink">{row.name}</p>
            <p className="text-xs text-ink-secondary">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: "Staff Role",
      render: (row) => {
        const badge = getRoleBadge(row.role);
        const hasCustom = Boolean(row.customPermissions);
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}>
              {badge.label}
            </span>
            {hasCustom && (
              <span className="inline-flex items-center rounded-md bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.2 text-[10px] font-medium" title="Custom permissions override active">
                Custom RBAC
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "lastLoginAt",
      header: "Last Active",
      render: (row) => <span className="text-[13px] text-ink">{formatDateTime(row.lastLoginAt)}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            row.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"
          }`}
        >
          {row.active ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  function actions(row: AdminUser) {
    return (
      <>
        <button
          type="button"
          onClick={() => handleEditClick(row)}
          className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
        >
          Edit Profile & Role
        </button>
        <button
          type="button"
          onClick={() => openPermissionsModal(row)}
          className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer font-medium text-primary-600"
        >
          <Sliders className="h-3.5 w-3.5" />
          Customize Permissions
        </button>
        <button
          type="button"
          onClick={() => toggleActive(row)}
          className={`flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm cursor-pointer ${
            row.active ? "text-danger hover:bg-danger-bg" : "text-success hover:bg-success-bg"
          }`}
        >
          {row.active ? "Deactivate" : "Activate"}
        </button>
      </>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-ink">Staff & Admin Users</h3>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              Manage internal operators, roles, and individualized module access overrides
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)} leftIcon={<UserPlus className="h-4 w-4" />}>
            Add Staff User
          </Button>
        </div>

        <div className="overflow-x-auto">
          <DataTable
            columns={columns}
            data={users}
            rowKey={(row) => row.id}
            actions={actions}
            pageSize={10}
            pageSizeOptions={[5, 10, 25]}
          />
        </div>
      </div>

      {/* Add Staff Modal */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add New Staff User"
        size="md"
        confirmLabel="Create User"
        onConfirm={saveAdmin}
        confirmLoading={saving}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Full Name"
            required
            placeholder="e.g. John Doe"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Email Address"
            type="email"
            required
            placeholder="staff@mandatepro.in"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Mobile Number"
            placeholder="10-digit mobile"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          />
          <Select
            label="Assigned Role"
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          />
        </div>
        <div className="mt-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Initial Password"
                type="text"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <Button variant="secondary" onClick={() => generatePassword("add")}>
              Generate
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Staff Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Edit Staff: ${editingUser?.name || ""}`}
        size="md"
        confirmLabel="Update Staff"
        onConfirm={saveEditUser}
        confirmLoading={saving}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Full Name"
            required
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <Input
            label="Email Address"
            type="email"
            required
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          />
          <Input
            label="Mobile Number"
            value={editForm.mobile}
            onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
          />
          <Select
            label="Assigned Role"
            options={ROLE_OPTIONS}
            value={editForm.role}
            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
          />
        </div>
        <div className="mt-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Reset Password (optional)"
                type="text"
                placeholder="Leave blank to preserve existing password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              />
            </div>
            <Button variant="secondary" onClick={() => generatePassword("edit")}>
              Generate
            </Button>
          </div>
        </div>
      </Modal>

      {/* User Custom Permissions Modal */}
      <Modal
        open={permModalOpen}
        onClose={() => setPermModalOpen(false)}
        title={`Customize Permissions — ${permUser?.name || ""}`}
        size="lg"
        confirmLabel={savingPerms ? "Saving..." : "Save User Permissions"}
        onConfirm={saveUserPermissions}
        confirmLoading={savingPerms}
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-slate-50/70 p-3.5">
            <div>
              <p className="text-sm font-semibold text-ink">Role Default vs. Custom Override</p>
              <p className="text-xs text-ink-secondary">
                Currently assigned role: <span className="font-semibold text-ink">{getRoleBadge(permUser?.role || "").label}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-ink cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-line text-primary-600 focus:ring-primary-500 mr-1.5"
                  checked={overrideEnabled}
                  onChange={(e) => setOverrideEnabled(e.target.checked)}
                />
                Enable custom overrides for this user
              </label>
            </div>
          </div>

          {!overrideEnabled ? (
            <div className="rounded-lg border border-dashed border-line bg-white p-6 text-center text-sm text-ink-secondary">
              <Shield className="mx-auto h-8 w-8 text-slate-400 mb-2" />
              <p className="font-medium text-ink">Inheriting Role Defaults</p>
              <p className="text-xs mt-1">
                This user currently inherits all permissions configured in the RBAC Role Matrix for their role.
                Check "Enable custom overrides" above to grant or revoke specific module permissions.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-line bg-slate-50">
                    <th className="py-2.5 px-4 font-semibold text-ink">MODULE</th>
                    <th className="py-2.5 px-4 font-semibold text-ink text-center">PERMISSIONS (V, C, E, A, D)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {MODULE_LIST.map((mod) => {
                    const modPerms = customPerms[mod.key] || { view: false, create: false, edit: false, approve: false, delete: false };
                    return (
                      <tr key={mod.key} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-4 font-medium text-ink capitalize">{mod.label}</td>
                        <td className="py-2.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {ACTIONS.map((act) => {
                              const granted = Boolean(modPerms[act.key]);
                              return (
                                <button
                                  key={act.key}
                                  type="button"
                                  onClick={() => toggleUserPerm(mod.key, act.key)}
                                  className={`flex h-6 w-6 items-center justify-center rounded border text-xs font-semibold cursor-pointer ${
                                    granted
                                      ? act.color + " shadow-xs ring-1 ring-black/5"
                                      : "bg-white text-slate-300 border-dashed border-slate-200 hover:border-slate-400 hover:text-slate-600"
                                  }`}
                                >
                                  {act.short}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}